// Renewgrant.jsx — wide, no scroll, single textarea (free text with inline quantities), no Top button
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import numberToWords from "number-to-words";
import "./GrantForm.css";
import GoldValuationModal from "./GoldValuationModal";

/* ===================== Server-time helpers ===================== */
const OFFSET_KEY = "serverOffsetMs_v1";

async function fetchServerNowUtc() {
  try {
    const res = await fetch(`${window.location.origin}/`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}
  try {
    const res = await fetch(`${window.location.origin}/favicon.ico`, { cache: "no-store" });
    const h = res.headers.get("date");
    if (h) return new Date(h);
  } catch {}
  return new Date(); // fallback only if server unreachable
}

async function ensureServerOffset() {
  const cached = sessionStorage.getItem(OFFSET_KEY);
  if (cached != null && !Number.isNaN(Number(cached))) return Number(cached);
  const serverNow = await fetchServerNowUtc();
  const offset = serverNow.getTime() - Date.now();
  sessionStorage.setItem(OFFSET_KEY, String(offset));
  return offset;
}

function businessNow() {
  const raw = sessionStorage.getItem(OFFSET_KEY);
  const offset = raw == null ? 0 : Number(raw);
  return new Date(Date.now() + (Number.isFinite(offset) ? offset : 0));
}
/* =============================================================== */

/* Helpers */
const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Same expiry rule used by Grant and enforced again by the backend.
const calculatePawnExpiry = (grantDate) => {
  const year = grantDate.getFullYear();
  const month = grantDate.getMonth();
  const day = grantDate.getDate();

  const targetMonthIndex = month + 6;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();

  if (day > lastDayOfTargetMonth) {
    return new Date(targetYear, targetMonth, lastDayOfTargetMonth);
  }

  return new Date(targetYear, targetMonth, day - 1);
};
const convertToISO = (dateStr) => {
  const parts = (dateStr || "").split("-");
  if (parts.length !== 3) return "";
  const month = new Date(Date.parse(parts[1] + " 1, 2000")).getMonth() + 1;
  return `${parts[2]}-${String(month).padStart(2, "0")}-${parts[0]}T00:00:00`;
};

// legacy utility (keep for downstream compatibility)
const splitItems = (raw) =>
  (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

// ===== NEW HELPERS =====
const sanitizeNumber = (s = "") => (s || "").replace(/,/g, "").trim();

/**
 * Count items by summing quantities found in free text.
 * Supports:
 *   • “3N” / “3 N” → +3
 *   • bare “1 GOLD RING” → +1 (skips purity like 916/750 and units like G/CT/K)
 * Falls back to comma-split count if text present but no explicit quantities.
 */
const countItems = (text = "") => {
  const src = (text || "").toUpperCase();
  let total = 0;

  // 1) "3N" or "3 N"
  const nTok = /\b(\d+)\s*N\b/g;
  let m;
  while ((m = nTok.exec(src))) total += parseInt(m[1], 10);

  // 2) Bare counts: number + word (not units)
  const bare = /\b(\d+)\s+(?!G(?:M|MS)?\b|GRAMS?\b|KG\b|OZ\b|CT\b|CARAT\S*\b|K\b|KT\b)\w+/g;
  const skipPurity = new Set([999, 916, 905, 900, 875, 750, 585, 417, 375]);
  while ((m = bare.exec(src))) {
    const n = parseInt(m[1], 10);
    if (skipPurity.has(n)) continue;
    total += n;
  }

  // 3) Fallback to comma-separated items if nothing matched
  if (total === 0 && src.trim().length > 0) {
    total = src
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;
  }
  return total;
};

const toWords = (num) => {
  const clean = sanitizeNumber(num);
  return !clean || isNaN(Number(clean))
    ? ""
    : numberToWords.toWords(Number(clean)).toUpperCase() + " DOLLARS ONLY";
};

export default function Renewgrant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialWeights, setInitialWeights] = useState(null);
  const [goldDetails, setGoldDetails] = useState(null);

  // from previous page
  const { oldReceiptNo, amountCollected } = location.state || {};

  // normalize incoming items
  const initialItemsInput = Array.isArray(location.state?.items)
    ? location.state.items
        .map((it) => (typeof it === "string" ? it : it?.value || ""))
        .filter(Boolean)
        .join(", ")
    : (location.state?.items || location.state?.item || "");

  // initial values
  const [itemsInput, setItemsInput] = useState((initialItemsInput || "").toUpperCase());
  const [remark, setRemark] = useState(location.state?.remark || "");
  const [principal, setPrincipal] = useState(
    location.state?.principal != null
      ? String(location.state.principal)
      : location.state?.amount != null
      ? String(location.state.amount)
      : ""
  );
  const [weight, setWeight] = useState(
    location.state?.weight != null ? String(location.state.weight) : ""
  );
  const [valuation, setValuation] = useState(
    location.state?.valuation != null
      ? String(location.state.valuation)
      : location.state?.valuationAmount != null
      ? String(location.state.valuationAmount)
      : ""
  );

  const [staffCode, setStaffCode] = useState("");
  const [grantDate, setGrantDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [serverReady, setServerReady] = useState(false);

  // Tab-to-expand lookups
  const [itemMap, setItemMap] = useState({});
  const [materialMap, setMaterialMap] = useState({});
  const [conditionMap, setConditionMap] = useState({});
  const itemsRef = useRef(null);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Prevent stale gold details from a previous ticket being reused accidentally.
    try { localStorage.removeItem("pawnGoldDetails"); } catch {}

    // ensure server offset before deriving dates
    let mounted = true;
    (async () => {
      await ensureServerOffset();
      if (!mounted) return;

      // lookups
      axios.get("https://www.lianfoong.com/api/items/getAllItems").then((res) => {
        const map = {};
        (res.data || []).forEach((it) => {
          if (it?.code) map[it.code.toLowerCase()] = it.itemDesc || it.code;
        });
        setItemMap(map);
      }).catch(() => {});

      axios.get("https://www.lianfoong.com/api/materials/getAllMaterials").then((res) => {
        const map = {};
        (res.data || []).forEach((m) => {
          if (m?.code) map[m.code.toLowerCase()] = m.material || m.code;
        });
        setMaterialMap(map);
      }).catch(() => {});

      axios.get("https://www.lianfoong.com/api/item-conditions/getAllItemConditions").then((res) => {
        const map = {};
        (res.data || []).forEach((c) => {
          if (c?.code) map[c.code.toLowerCase()] = c.itemCondition || c.code;
        });
        setConditionMap(map);
      }).catch(() => {});

      axios
        .get("https://www.lianfoong.com/api/staff/session", { withCredentials: true })
        .then((res) => {
          if (res.data?.staffCode) setStaffCode(res.data.staffCode);
        })
        .catch(() => {});

      // dates from SERVER time (not PC)
      const today = businessNow();
      const grantCalendarDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const expiry = calculatePawnExpiry(grantCalendarDate);
      setGrantDate(formatDate(grantCalendarDate));
      setExpiryDate(formatDate(expiry));
      setServerReady(true);

      // load previous weights/valuation for renewal (unchanged)
      if (location.state?.oldReceiptNo) {
        const pledge = location.state.oldReceiptNo;
        axios
          .get("https://www.lianfoong.com/api/pawn-gold/pledge", { params: { pledgeNo: pledge } })
          .then(async (res) => {
            const pg = res.data || {};
            const prevWeights = {
              "999":  String(pg.gold1Qty ?? ""),
              "24K":  String(pg.gold2Qty ?? ""),
              "23K":  String(pg.gold3Qty ?? ""),
              "22K":  String(pg.gold4Qty ?? ""),
              "20K":  String(pg.gold5Qty ?? ""),
              "18K":  String(pg.gold6Qty ?? ""),
              "14K":  String(pg.gold7Qty ?? ""),
              "9K":   String(pg.gold8Qty ?? ""),
              "STONE":String(pg.gold9Qty ?? "")
            };
            setInitialWeights(prevWeights);

            const { data: priceRows } = await axios.get("https://www.lianfoong.com/api/gold-prices");
            const prices = (priceRows?.[0]) || {};
            const keys = ["999","24K","23K","22K","20K","18K","14K","9K","STONE"];
            const sumVal =
              keys.reduce((acc, k) => acc + (Number(prevWeights[k] || 0) * Number(prices[k] || 0)), 0)
              + Number(pg.valAddAmt || 0);
            const sumW =
              keys.reduce((acc, k) => acc + Number(prevWeights[k] || 0), 0);

            setWeight(String(sumW.toFixed(1)));
            setValuation(String(sumVal.toFixed(2)));

            const loadedGoldDetails = {
              weights: prevWeights,
              prices: prices,
              valAdd: String(pg.valAddAmt ?? "0"),
              totalWeight: sumW,
              totalValue: sumVal
            };

            setGoldDetails(loadedGoldDetails);

            localStorage.setItem("pawnGoldDetails", JSON.stringify(loadedGoldDetails));
          })
          .catch(() => {});
      }
    })();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab-to-expand inside the single textarea (spaces and commas are boundaries)
  const handleItemsKeyDown = (e) => {
    if (e.key !== "Tab" || e.shiftKey) return;
    const textarea = itemsRef.current;
    if (!textarea) return;

    const value = itemsInput || "";
    const cursor = textarea.selectionStart ?? value.length;

    const leftSpace = value.lastIndexOf(" ", Math.max(0, cursor - 1));
    const leftComma = value.lastIndexOf(",", Math.max(0, cursor - 1));
    const start = Math.max(leftSpace, leftComma) + 1;

    const rightSpace = value.indexOf(" ", cursor);
    const rightComma = value.indexOf(",", cursor);
    const rights = [rightSpace, rightComma].filter((n) => n !== -1);
    const end = rights.length ? Math.min(...rights) : value.length;

    const token = value.slice(start, end).trim();
    if (!token) return;

    const key = token.toLowerCase();
    const hit = itemMap[key] ?? materialMap[key] ?? conditionMap[key];
    if (!hit) return;

    e.preventDefault();
    const replacement = String(hit).toUpperCase();
    const newValue = value.slice(0, start) + replacement + value.slice(end);
    setItemsInput(newValue);

    requestAnimationFrame(() => {
      const pos = start + replacement.length;
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  };

  // quantity-based count
  const itemsCount = countItems(itemsInput);

  const handleSubmit = () => {
    const itemsArray = splitItems(itemsInput);

    const transactionData = {
      activeRecord: true,
      item: (itemsInput || "").trim(),
      weight: sanitizeNumber(weight),
      amount: sanitizeNumber(principal),
      valuationAmount: sanitizeNumber(valuation),
      remark,
      noOfItem: itemsCount,
      oldReceiptNo,
      grantDate: convertToISO(grantDate),
      expiryDate: convertToISO(expiryDate),
      handler: staffCode,

      // IMPORTANT: carry per-karat gold breakdown to Renewpawner
      goldDetails,
    };

    navigate("/renew-pawner", {
      state: { transactionData, oldReceiptNo, items: itemsArray, staffCode, amountCollected },
    });
  };

  return (
    <div className="grf-page grf-nosroll">
      <div className="grant-form grant-form--wide">
        {/* header */}
        <div className="grf-header">
          <h2 className="grf-title">Grant Form (Renew)</h2>
          <div className="grf-meta">
            <span className="grf-badge">Items: {itemsCount}</span>
          </div>
        </div>

        {/* body — wide, no internal scrolling */}
        <div className="grf-body-noscroll">
          {!serverReady && (
            <p style={{ color: "#555", marginTop: 0, marginBottom: 8 }}>
              Loading server time… dates will use server clock.
            </p>
          )}

          {/* Single items textarea */}
          <div className="grf-section-title"></div>
          <div className="grf-item-single">
            <label className="grf-label">Items</label>
            <textarea
              ref={itemsRef}
              className="grf-input grf-textarea"
              placeholder={`Examples:
• 1 GOLD BAR 1 GOLD RING
• RNG 916 3N, BRCL 916 2N, EARR 750 3.5G`}
              value={itemsInput}
              onChange={(e) => setItemsInput((e.target.value || "").toUpperCase())}
              onKeyDown={handleItemsKeyDown}
              rows={4}
            />
          </div>

          <div className="grf-grid-12">
            <div className="grf-field col-6">
              <label className="grf-label">Remark</label>
              <input
                type="text"
                className="grf-input"
                value={remark}
                onChange={(e) => setRemark((e.target.value || "").toUpperCase())}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Amount</label>
              <div className="grf-stack">
                <input
                  type="text"
                  className="grf-input"
                  value={principal}
                  onChange={(e) => setPrincipal(e.target.value.replace(/[^\d.]/g, ""))}
                />
                {principal && <span className="grf-amount-label">{toWords(principal)}</span>}
              </div>
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Weight</label>
              <input
                type="text"
                className="grf-input"
                value={weight}
                onChange={(e) => setWeight(e.target.value.replace(/[^\d.]/g, ""))}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">No. of Items</label>
              <input type="text" className="grf-input" value={itemsCount} readOnly />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Valuation</label>
              <div className="grf-inline">
                <input type="text" className="grf-input" value={valuation} readOnly />
                <button type="button" className="grf-open" onClick={() => setIsModalOpen(true)}>
                  Open
                </button>
              </div>
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Grant Date</label>
              <input type="text" className="grf-input" value={grantDate} readOnly />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Expiry Date</label>
              <input type="text" className="grf-input" value={expiryDate} readOnly />
            </div>
          </div>
        </div>

        {/* footer — no Top button */}
        <div className="grf-footer-nosticky">
          <button type="button" className="grf-btn grf-btn--ghost" onClick={() => navigate("/dashboard")}>
            Back
          </button>
          <button type="button" className="grf-btn grf-btn--primary" onClick={handleSubmit}>
            Next
          </button>
        </div>

        {/* modal */}
        <GoldValuationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={(newWeight, newValuation, details) => {
            const w = sanitizeNumber(newWeight);
            setWeight(w);
            const v = Number(sanitizeNumber(newValuation));
            setValuation(Number.isFinite(v) ? v.toFixed(2) : "");

            setGoldDetails(details);

            try { localStorage.setItem("pawnGoldDetails", JSON.stringify(details)); } catch {}
          }}
          initialWeights={initialWeights}
        />
      </div>
    </div>
  );
}
